import { useEffect, useMemo } from "react"
import { useQuery } from "urql"

import type { Query, User } from "@/graphql/codegen/graphql"
import { UsersListDocument } from "@/graphql/codegen/graphql"
import { AccountManagerListSchema } from "@/schemas/accountManagersListSchema"
import { useAccountManagerStore } from "@/stores/accountManagerStore"

interface UseAccountManagersParams {
  query?: string
}

interface UseAccountManagersReturn {
  visibleManagers: User[]
  fetching: boolean
}

// Returns the users of a loaded list, or undefined while it is loading, failed or has an unexpected shape.
export const parseUserList = (data: Pick<Query, "user"> | undefined): User[] | undefined => {
  if (data?.user?.list?.__typename !== "UserListSuccess") return undefined

  // Validate the data with Zod
  try {
    return AccountManagerListSchema.parse(data.user.list.users)
  } catch (error) {
    console.error("Invalid data format", error)
    return undefined
  }
}

// The account managers, for choosing who manages an account. Use useManagers to list product managers as well.
export const useAccountManagers = ({ query = "" }: UseAccountManagersParams): UseAccountManagersReturn => {
  // Get state and actions from the Zustand store
  const { visibleManagers: storedAccountManagers, limit, setVisibleManagers } = useAccountManagerStore()

  const searchFilter = query && query?.length > 1 && { query }

  const [{ data, fetching }] = useQuery<Pick<Query, "user">>({
    query: UsersListDocument,
    variables: {
      input: { filter: { ...searchFilter, permissions: ["account_manager"] }, limit },
    },
  })

  const accountManagers = useMemo(() => parseUserList(data), [data])

  useEffect(() => {
    if (accountManagers) setVisibleManagers(accountManagers)
  }, [accountManagers, setVisibleManagers])

  return {
    visibleManagers: accountManagers ?? storedAccountManagers,
    fetching,
  }
}
